// journal/model — the Journal's Model: the page of periods for the chosen
// scale, plus everything the author is doing to it. Every state is explicit:
// the page is loading, loaded or failed; an own-text draft is being edited,
// saved or failed; a write the server refused is a named problem.
//
// Optimistic writes: a correction, an unlock and a consent change apply to the
// loaded page at once; `rollback` keeps what each replaced, so a refusal puts
// it back. Pure data; projections live in `view-model.ts`.

import type { NewType } from "@bilda/utils";
import type {
    AssistantText,
    JournalPage,
    JournalScale,
} from "../../app/ports/journal.js";
import type { EntityHistory } from "../../components/WorkspaceHome/JournalTimeline.js";

type PageState =
    | NewType<"Page.Loading">
    | NewType<"Page.Loaded", { readonly page: JournalPage }>
    | NewType<"Page.Failed">;

export type Voice = "own" | "assistant";

// The author's own text while it differs from what the server holds.
// `editing`: typed, not yet committed; `saving`: committed, awaiting the
// answer; `failed`: the server did not take it (kept, offered again).
type OwnDraft = {
    readonly text: string;
    readonly phase: "editing" | "saving" | "failed";
};

// The one correction of an assistant text in progress (view-ephemeral draft).
type Correction = {
    readonly periodId: string;
    readonly draft: string;
};

// A write the server refused, named so the view can say which.
export type FailedWrite =
    | "correct"
    | "unlock"
    | "summarize"
    | "consent"
    | "erase_all";
export type Problem =
    | NewType<"Problem.None">
    | NewType<
          "Problem.WriteFailed",
          // `periodId` null: a journal-wide write (consent, erase all).
          { readonly write: FailedWrite; readonly periodId: string | null }
      >;

export const noProblem: Problem = { _tag: "Problem.None" };

export type JournalModel = {
    readonly scale: JournalScale;
    // How far back the page reads ("YYYY-MM-DD"); null = the default span.
    readonly from: string | null;
    readonly page: PageState;
    // "Show older" asked; the loaded page stays until the wider one lands.
    readonly older: "idle" | "loading";
    // A period to scroll into view once the page holding it is loaded.
    readonly reveal: string | null;
    // Periods the author opened (several may be open at once).
    readonly open: ReadonlySet<string>;
    // Corrected periods whose assistant original is shown.
    readonly originals: ReadonlySet<string>;
    // The voice the author chose per period; absent = the default.
    readonly voices: ReadonlyMap<string, Voice>;
    // The activity row whose history is shown, per day.
    readonly selectedRows: ReadonlyMap<string, string>;
    // Resources' own histories read so far, by resource id.
    readonly histories: ReadonlyMap<string, EntityHistory>;
    readonly drafts: ReadonlyMap<string, OwnDraft>;
    readonly correction: Correction | null;
    // What an optimistic assistant write replaced, by period.
    readonly rollback: ReadonlyMap<string, AssistantText | null>;
    // Periods whose summary was asked for and is being written.
    readonly summarizing: ReadonlySet<string>;
    // "Erase all your texts": asked (awaiting the author's confirmation),
    // or on its way.
    readonly eraseAll: "idle" | "confirming" | "erasing";
    readonly problem: Problem;
};

export const initialJournalModel: JournalModel = {
    scale: "day",
    from: null,
    page: { _tag: "Page.Loading" },
    older: "idle",
    reveal: null,
    open: new Set(),
    originals: new Set(),
    voices: new Map(),
    selectedRows: new Map(),
    histories: new Map(),
    drafts: new Map(),
    correction: null,
    rollback: new Map(),
    summarizing: new Set(),
    eraseAll: "idle",
    problem: noProblem,
};
